import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import useAuth from "../../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Select } from "flowbite-react";
import CountryOptions from "../../../public/CountryOptions";
import moment from "moment";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { useUpdateUserProfile } from "../../../../hooks/users";
import { PhoneNumberWithVerification } from "../../../atoms/PhoneNumberWithVerification";
import { useEffect } from "react";
import { usePermissions } from "../../../../utils/permissions";
import { FieldLabel } from "../../../atoms/FieldLabel";

const schema = Joi.object({
  new_user_user_name: Joi.string()
    .trim()
    .min(3)
    .max(30)
    .optional()
    .allow(null, "")
    .label("Username"),

  new_user_first_name: Joi.string()
    .trim()
    .min(3)
    .max(30)
    .required()
    .label("First name"),

  new_user_last_name: Joi.string()
    .trim()
    .min(3)
    .max(150)
    .optional()
    .required()
    .label("Last name"),

  new_user_birth_date: Joi.date()
    .optional()
    .allow(null, "")
    .label("Birth date"),

  new_user_country_code: Joi.string()
    .trim()
    .optional()
    .allow(null, "")
    .label("Country code"),

  new_user_phone: Joi.string()
    .trim()
    .optional()
    .allow(null, "")
    .label("Phone number"),
});

const ProfileForm = ({
  entity_identification,
  data,
}: {
  entity_identification: string;
  data: any;
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const hasPhoneValidationAccess = permissions.hasFeatureAccess(
    "PHONE_NUMBER_VALIDATION"
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: {
      new_user_first_name: data.user_first_name,
      new_user_last_name: data.user_last_name,
      new_user_birth_date: data.user_birth_date
        ? new Date(data.user_birth_date).toISOString().substring(0, 10)
        : "",
      new_user_country_code: data.user_country_code,
      new_user_user_name: data.user_user_name,
      new_user_phone: data.user_phone,
    },
  });

  const { mutate, isLoading } = useUpdateUserProfile();

  useEffect(() => {
    if (data) {
      reset({
        new_user_first_name: data.user_first_name || "",
        new_user_last_name: data.user_last_name || "",
        new_user_birth_date: data.user_birth_date
          ? new Date(data.user_birth_date).toISOString().substring(0, 10)
          : "",
        new_user_phone: data.user_phone || "",
        new_user_user_name: data.user_user_name || "",
        new_user_country_code: data.user_country_code || "",
      });
    }
  }, [data]);

  const onSubmit = (data: any) => {
    mutate({
      customer_identification: customer_identification,
      entity_identification: entity_identification,
      user_identification: customer_identification,
      data: data,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="grid grid-cols-12 gap-4">
        <div className="lg:col-span-4 sm:col-span-6 col-span-12">
          <div className="mb-4">
            <CustomInput
              name="new_user_first_name"
              control={control}
              error={errors.new_user_first_name}
              helperText={errors.new_user_first_name?.message}
              label={t("dashboard.userProfile.profileForm.label-01") as string}
              className="shadow rounded-md"
            />
          </div>
        </div>
        <div className="lg:col-span-4 sm:col-span-6 col-span-12">
          <div className="mb-3">
            <CustomInput
              name="new_user_last_name"
              control={control}
              error={errors.new_user_last_name}
              helperText={errors.new_user_last_name?.message}
              label={t("dashboard.userProfile.profileForm.label-02") as string}
              className="shadow rounded-md"
            />
          </div>
        </div>
        <div className="lg:col-span-4 sm:col-span-6 col-span-12">
          <div className="mb-3">
            <CustomInput
              name="new_user_user_name"
              control={control}
              error={errors.new_user_user_name}
              helperText={errors.new_user_user_name?.message}
              className="shadow rounded-md"
              label={t("dashboard.userProfile.profileForm.label-03") as string}
            />
          </div>
        </div>
        <div className="lg:col-span-4 sm:col-span-6 col-span-12">
          <div>
            <CustomInput
              type="date"
              name="new_user_birth_date"
              control={control}
              error={errors.new_user_birth_date}
              helperText={errors.new_user_birth_date?.message}
              label={t("dashboard.userProfile.profileForm.label-04") as string}
              max={moment().format("YYYY-MM-DD")}
              className="shadow rounded-md max-h-[42px]"
            />
          </div>
        </div>
        <div className="lg:col-span-4 sm:col-span-6 col-span-12">
          <div>
            <FieldLabel
              className="block text-sm font-medium text-gray-700 mb-3"
              htmlFor="new_user_country_code"
            >
              {t("dashboard.userProfile.profileForm.label-05")}
            </FieldLabel>
            <Select
              translate="no"
              id="new_user_country_code"
              color={!!errors.new_user_country_code ? "failure" : "gray"}
              className="shadow rounded-md"
              {...register("new_user_country_code")}
            >
              <option value="">
                {t("dashboard.userProfile.profileForm.select-option")}
              </option>
              <CountryOptions />
            </Select>
            <p className="mt-1 text-xs text-red-600">
              {errors.new_user_country_code?.message as any}
            </p>
          </div>
        </div>

        <div className="lg:col-span-4 sm:col-span-6 col-span-12">
          <div className="mb-3">
            <PhoneNumberWithVerification
              defaultCountryCode={watch("new_user_country_code")}
              name="new_user_phone"
              control={control}
              error={errors.new_user_phone}
              helperText={errors.new_user_phone?.message}
              label={t("dashboard.userProfile.profileForm.label-06") as string}
              maxLength={13}
              userIdentification={customer_identification}
              entityIdentification={entity_identification}
              customerIdentification={customer_identification}
              userIsActive={true}
              hasPhoneValidationAccess={hasPhoneValidationAccess}
            />
          </div>
        </div>
      </div>

      <div className="self-stretch h-px bg-gray-300 mt-5" />

      <div className="flex align-center justify-end mt-4">
        <CustomButton
          type="submit"
          disabled={isLoading}
          isLoading={isLoading}
          variant="primary"
          title={t("dashboard.userProfile.profileForm.btn-01")}
        />
      </div>
    </form>
  );
};

export default ProfileForm;
